import { Utils } from "@/plugins/utils"
import moment from "moment"

export default {
	namespaced: true,

	state: {
		lstvdq: [],
		vdq: {},
		loading: false,
		itemsPerPage: 20,

		searchParams: {
			search: "",
			vdqcliente: "",
			since: moment().subtract(1, "year").format("YYYY-MM-DD 00:00"),
			until: moment().format("YYYY-MM-DD 23:59"),
			documentStatus: [0, 1],
			orderStatus: [],
		},

		emailCapability: {
			result: null,
			loading: false,
		},

		whatsappCapability: {
			result: null,
			loading: false,
		},

		hasNext: false,
	},

	getters: {
		isInUpdateState: state => {
			const { vdqvisita } = state.vdq
			return !!vdqvisita?.toString().trim()
		},

		isInInsertState: (state, getters) => !getters.isInUpdateState,

		vdqcliente: state => state.vdq.vdqcliente ?? "",
		vdqvend: state => state.vdq.vdqvend ?? "",
	},

	mutations: {
		setEmailCapabilityLoading(state, value) {
			state.emailCapability.loading = value
		},

		setWhatsappCapabilityLoading(state, value) {
			state.whatsappCapability.loading = value
		},

		hydrateEmailSendCapability(state, result) {
			state.emailCapability.result = result
			state.emailCapability.loading = false
		},

		hydrateWhatsappSendCapability(state, result) {
			state.whatsappCapability.result = result
			state.whatsappCapability.loading = false
		},

		setLoading(state, value) {
			state.loading = value
		},

		initForm(state) {
			state.vdq = {
				vdqvisita: "",
				vdqcliente: "",
				vdqvend: "",
				vdqenderid: "",
				vdqlista: "",
				customerHasWithdrawn: false,

				cf1info: {},
				cfbinfo: {},

				items: {
					soldItems: new Map(),
					returnedItems: new Map(),
				},
			}
		},

		setParamsDataTable(state, payload) {
			state.lstvdq = payload.items
			state.itemsPerPage = payload.limit
			state.hasNext = payload.hasNext
		},

		set(state, vdq) {
			state.vdq = {
				cf1info: {},
				cfbinfo: {},
				items: { soldItems: new Map(), returnedItems: new Map() },
				...(vdq || {}),
			}

			state.vdq.items ??= {
				soldItems: new Map(),
				returnedItems: new Map(),
			}
		},

		setItemsMaps(state, { soldItems, returnedItems }) {
			state.vdq.items = {
				soldItems: soldItems instanceof Map ? soldItems : new Map(),
				returnedItems: returnedItems instanceof Map ? returnedItems : new Map(),
			}
		},
	},

	actions: {
		initForm({ commit, dispatch, rootState }) {
			commit("initForm")

			dispatch("vdp/initForOrder", null, { root: true })
			commit("setItemsMaps", {
				soldItems: rootState.vdp.soldItems,
				returnedItems: rootState.vdp.returnedItems,
			})
		},

		async search(store, { limit = 20, page = 1 }) {
			store.commit("setLoading", true)

			const params = {
				...store.state.searchParams,
				limit,
				page,
			}

			try {
				const response = await this._vm.$axios.get("/Oryxplace/OryxplaceOrder/Search", { params })
				const payload = Utils.ValidateAxiosResponse(response.data)
				store.commit("setParamsDataTable", payload)
				return true
			} catch (error) {
				store.state.lstvdq = []
				store.dispatch(
					"parameters/openDialog",
					{ titleText: "Erro", titleColor: "red", text: error.message },
					{ root: true },
				)
				return false
			} finally {
				store.commit("setLoading", false)
			}
		},

		async getOrderStatusCount(store) {
			try {
				const response = await this._vm.$axios.get("/Oryxplace/OryxplaceOrder/GetOrderStatusCounts", {
					params: { ...store.state.searchParams },
				})
				const data = Utils.ValidateAxiosResponse(response.data)
				return Object.fromEntries(data.map(x => [x.vdqstatus, x.total]))
			} catch (error) {
				store.dispatch(
					"parameters/openDialog",
					{ titleText: "Erro", titleColor: "red", text: error.message },
					{ root: true },
				)
				return {}
			}
		},

		async find({ commit, dispatch, rootState }, vdqvisita) {
			commit("setLoading", true)

			try {
				const response = await this._vm.$axios.get("/Oryxplace/OryxplaceOrder/Find", {
					params: { vdqvisita },
				})

				const vdq = Utils.ValidateAxiosResponse(response.data)

				await dispatch("vdp/initForOrder", null, { root: true })

				commit("set", vdq)
				commit("setItemsMaps", {
					soldItems: rootState.vdp.soldItems,
					returnedItems: rootState.vdp.returnedItems,
				})

				return vdq
			} catch (error) {
				await dispatch(
					"parameters/openDialog",
					{ titleText: "Erro", titleColor: "red", text: error.message },
					{ root: true },
				)
				return {}
			} finally {
				commit("setLoading", false)
			}
		},

		async getAllDocumentsByVdq({ commit, dispatch }, { vdqvisita }) {
			commit("setLoading", true)
			try {
				const response = await this._vm.$axios.get("/Oryxplace/OryxplaceOrder/GetAllDocumentsByVdq", {
					params: { vdqvisita },
				})
				return Utils.ValidateAxiosResponse(response.data)
			} catch (error) {
				await dispatch(
					"parameters/openDialog",
					{ titleText: "Erro", titleColor: "red", text: error.message },
					{ root: true },
				)
				return null
			} finally {
				commit("setLoading", false)
			}
		},

		async getAllInstallmentsByVdq({ commit, dispatch }, { vdqvisita }) {
			commit("setLoading", true)
			try {
				const response = await this._vm.$axios.get("/Oryxplace/OryxplaceOrder/GetAllInstallmentsByVdq", {
					params: { vdqvisita },
				})
				return Utils.ValidateAxiosResponse(response.data)
			} catch (error) {
				await dispatch(
					"parameters/openDialog",
					{ titleText: "Erro", titleColor: "red", text: error.message },
					{ root: true },
				)
				return null
			} finally {
				commit("setLoading", false)
			}
		},

		async getAllCustomersWithOrders({ dispatch }, { text = "" }) {
			try {
				const response = await this._vm.$axios.get("/Oryxplace/OryxplaceOrder/GetAllCustomersWithOrders", {
					params: { search: text },
				})
				return Utils.ValidateAxiosResponse(response.data)
			} catch (error) {
				dispatch("parameters/openDialog", { titleText: "Erro", titleColor: "red", text: error.message }, { root: true })
				return []
			}
		},

		async getAllItemsByVdq({ commit, dispatch, rootState }, vdqvisita) {
			try {
				await dispatch("vdp/fetchByVdq", vdqvisita, { root: true })
				commit("setItemsMaps", {
					soldItems: rootState.vdp.soldItems,
					returnedItems: rootState.vdp.returnedItems,
				})
				return {
					soldItems: rootState.vdp.soldItems,
					returnedItems: rootState.vdp.returnedItems,
				}
			} catch (error) {
				await dispatch(
					"parameters/openDialog",
					{ titleText: "Erro", titleColor: "red", text: error.message },
					{ root: true },
				)
				return null
			}
		},

		async save({ state, dispatch, commit, rootState, rootGetters }) {
			dispatch(
				"parameters/openGlobalLoading",
				{ showLoadingGlobal: true, text: "Salvando", indeterminate: true },
				{ root: true },
			)

			try {
				if (!rootGetters["vdp/hasOrderItems"]) {
					dispatch(
						"parameters/showSnackBar",
						{
							show: true,
							timeout: 5000,
							text: "Adicione ao menos um item para salvar o pedido.",
							color: "red",
						},
						{ root: true },
					)
					return false
				}

				const payload = JSON.parse(JSON.stringify(state.vdq))

				payload.items = {
					soldItems: rootGetters["vdp/soldItemsList"],
					returnedItems: rootGetters["vdp/returnedItemsList"],
				}

				const response = await this._vm.$axios.post("/Oryxplace/OryxplaceOrder/Save", payload)
				const data = Utils.ValidateAxiosResponse(response.data)

				const relatPath = data.reportpath
				if (!!relatPath) {
					dispatch("print/openRelatsPath", [relatPath], { root: true })
				}

				await dispatch("vdp/hydrateFromOrderPayload", data.items, { root: true })

				const newVdq = { ...data }
				newVdq.items = {
					soldItems: rootState.vdp.soldItems,
					returnedItems: rootState.vdp.returnedItems,
				}

				commit("set", newVdq)
				commit("setItemsMaps", {
					soldItems: rootState.vdp.soldItems,
					returnedItems: rootState.vdp.returnedItems,
				})

				return newVdq
			} catch (error) {
				dispatch("parameters/openDialog", { titleText: "Erro", titleColor: "red", text: error.message }, { root: true })
				return false
			} finally {
				dispatch("parameters/openGlobalLoading", { showLoadingGlobal: false, text: "" }, { root: true })
			}
		},

		async delete({ state, dispatch }) {
			dispatch(
				"parameters/openGlobalLoading",
				{ showLoadingGlobal: true, text: "Cancelando", indeterminate: true },
				{ root: true },
			)
			try {
				const response = await this._vm.$axios.delete(`/Oryxplace/OryxplaceOrder/Delete/${state.vdq.vdqvisita}`)
				return Utils.ValidateAxiosResponse(response.data)
			} catch (error) {
				dispatch("parameters/openDialog", { titleText: "Erro", titleColor: "red", text: error.message }, { root: true })
				return null
			} finally {
				dispatch("parameters/openGlobalLoading", { showLoadingGlobal: false, text: "" }, { root: true })
			}
		},

		async batchUpdateVdqstatus({ dispatch }, { lstVdqvisita = [], newStatus = 0, vdqmotivo = "" } = {}) {
			dispatch(
				"parameters/openGlobalLoading",
				{ showLoadingGlobal: true, text: "Salvando", indeterminate: true },
				{ root: true },
			)

			try {
				const response = await this._vm.$axios.post("/Oryxplace/OryxplaceOrder/BatchUpdateVdqstatus", lstVdqvisita, {
					params: { newStatus, vdqmotivo },
				})
				return Utils.ValidateAxiosResponse(response.data)
			} catch (error) {
				dispatch("parameters/openDialog", { titleText: "Erro", titleColor: "red", text: error.message }, { root: true })
				return false
			} finally {
				dispatch("parameters/openGlobalLoading", { showLoadingGlobal: false, text: "" }, { root: true })
			}
		},

		async printOrderReport({ dispatch }, { vdqvisita, download = false }) {
			dispatch(
				"parameters/openGlobalLoading",
				{
					showLoadingGlobal: true,
					text: `${download ? "Baixando" : "Gerando"} relatório do pedido ${vdqvisita}`,
					indeterminate: true,
				},
				{ root: true },
			)

			try {
				const response = await this._vm.$axios.get("/Oryxplace/OryxplaceOrder/PrintOrderReport", {
					params: { vdqvisita, print: !download, printLocal: false },
				})

				const relatPath = Utils.ValidateAxiosResponse(response.data)

				if (relatPath) {
					if (download) dispatch("print/downloadFile", [relatPath], { root: true })
					else dispatch("print/openRelatsPath", [relatPath], { root: true })
				}

				return true
			} catch (error) {
				dispatch("parameters/openDialog", { titleText: "Erro", titleColor: "red", text: error.message }, { root: true })
				return false
			} finally {
				dispatch("parameters/openGlobalLoading", { showLoadingGlobal: false, text: "" }, { root: true })
			}
		},

		async sendOrderSummaryEmail({ dispatch, state }, { vdqvisita, customReceiver = "" } = {}) {
			dispatch(
				"parameters/openGlobalLoading",
				{ showLoadingGlobal: true, text: "Enviando e-mail", indeterminate: true },
				{ root: true },
			)

			try {
				const payload = {
					Vdqvisita: vdqvisita,
					CustomReceiver: String(customReceiver || "").trim(),
				}

				const response = await this._vm.$axios.post("/Oryxplace/OryxplaceOrder/SendOrderSummaryEmail", payload)

				const data = Utils.ValidateAxiosResponse(response.data)

				if (data === true) {
					dispatch(
						"parameters/showSnackBar",
						{
							show: true,
							timeout: 3000,
							text: "Resumo do pedido enviado por e-mail!",
							color: "green",
						},
						{ root: true },
					)
					return true
				}

				dispatch(
					"parameters/openDialog",
					{
						titleText: "Erro",
						titleColor: "red",
						text: "Não foi possível enviar o e-mail do resumo do pedido.",
					},
					{ root: true },
				)
				return false
			} catch (error) {
				dispatch(
					"parameters/openDialog",
					{
						titleText: "Erro",
						titleColor: "red",
						text: error.message,
					},
					{ root: true },
				)
				return false
			} finally {
				dispatch("parameters/openGlobalLoading", { showLoadingGlobal: false, text: "" }, { root: true })
			}
		},

		async sendOrderSummaryWhatsapp({ dispatch }, { vdqvisita } = {}) {
			dispatch(
				"parameters/openGlobalLoading",
				{ showLoadingGlobal: true, text: "Agendando envio de mensagem", indeterminate: true },
				{ root: true },
			)

			try {
				const response = await this._vm.$axios.post(
					"/Oryxplace/OryxplaceOrder/SendOrderSummaryWhatsApp",
					{},
					{ params: { vdqvisita } },
				)

				const data = Utils.ValidateAxiosResponse(response.data)

				if (data === true) {
					dispatch(
						"parameters/showSnackBar",
						{
							show: true,
							timeout: 3000,
							text: "Mensagem do resumo do pedido agendada! O cliente a vai receber em breve.",
							color: "green",
						},
						{ root: true },
					)
					return true
				}

				dispatch(
					"parameters/openDialog",
					{ titleText: "Erro", titleColor: "red", text: "Não foi possível agendar/enviar a mensagem." },
					{ root: true },
				)
				return false
			} catch (error) {
				dispatch("parameters/openDialog", { titleText: "Erro", titleColor: "red", text: error.message }, { root: true })
				return false
			} finally {
				dispatch("parameters/openGlobalLoading", { showLoadingGlobal: false, text: "" }, { root: true })
			}
		},

		async hydrateEmailSendCapability({ commit }, { receiver, checkConnectivity = false } = {}) {
			commit("setEmailCapabilityLoading", true)

			try {
				const response = await this._vm.$axios.get("/Oryxplace/OryxplaceOrder/ValidateEmailSendCapability", {
					params: { receiver, checkConnectivity },
				})

				commit("hydrateEmailSendCapability", Utils.ValidateAxiosResponse(response.data))
			} catch (error) {
				commit("hydrateEmailSendCapability", {
					success: false,
					reason: error.message,
				})
			} finally {
				commit("setEmailCapabilityLoading", false)
			}
		},

		async hydrateWhatsappSendCapability({ dispatch, commit }, { vdqvisita, cf1cliente = "" } = {}) {
			commit("setWhatsappCapabilityLoading", true)
			try {
				const response = await this._vm.$axios.get("/Oryxplace/OryxplaceOrder/ValidateWhatsappSendCapability", {
					headers: { "Content-Type": "application/json", company: localStorage.getItem("companyCode") },
					params: {
						vdqvisita: vdqvisita,
						cf1cliente: cf1cliente,
					},
				})
				commit("hydrateWhatsappSendCapability", Utils.ValidateAxiosResponse(response.data))
			} catch (error) {
				dispatch(
					"parameters/openDialog",
					{
						titleText: "Erro",
						titleColor: "red",
						text: error.message,
					},
					{ root: true },
				)
				return { success: false, reason: error.message }
			} finally {
				commit("setWhatsappCapabilityLoading", false)
			}
		},
		async changeStatus({ commit, dispatch }, { vdqvisita, status, reason }) {
			dispatch(
				"parameters/openGlobalLoading",
				{ showLoadingGlobal: true, text: "Alterando status", indeterminate: true },
				{ root: true },
			)
			try {
				const response = await this._vm.$axios.put(`/Oryxplace/OryxplaceOrder/ChangeOrderStatus`, null, {
					params: { vdqvisita, status, reason },
				})
				const data = Utils.ValidateAxiosResponse(response.data)
				commit("set", data)
				return true
			} catch (error) {
				dispatch(
					"parameters/openDialog",
					{
						titleText: "Erro",
						titleColor: "red",
						text: error.message,
					},
					{ root: true },
				)
				return false
			} finally {
				dispatch("parameters/openGlobalLoading", { showLoadingGlobal: false, text: "" }, { root: true })
			}
		},
	},
}
